import { useMemo } from 'react';

import { usePsfSharePageData } from 'src/app/pages/psf/share/pageContext';
import type { PsfSharePageData } from 'src/app/pages/psf/share/pageContext';
import { SHARE_CHANNELS } from 'src/app/pages/psf/share/shared/consts';
import type { ShareChannel } from 'src/app/pages/psf/share/shared/types';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useFilteredShareChannels } from 'src/app/shared/hooks/share';

import { usePsfShareChannelRemap } from '../usePsfShareChannelRemap';

type OldShareChannelName = Exclude<PsfSharePageData['featureConfigs']['shareButtonsListNonInitial'][0], ShareChannel>;
type OldShareChannelNameFromQuery = 'copylink' | 'facebook_message' | 'instagram_story';
const OLD_TO_NEW_SHARE_CHANNEL_MAPPING: Record<string, ShareChannel> = {
	copylink: 'copy',
	facebook_message: 'facebookMessenger',
	instagram_story: 'instagramStories',
} satisfies Record<OldShareChannelName | OldShareChannelNameFromQuery, ShareChannel>;

function mapOldShareChannels(channels: ReadonlyArray<OldShareChannelName | ShareChannel>): readonly ShareChannel[] {
	return channels.map((name) => OLD_TO_NEW_SHARE_CHANNEL_MAPPING[name] || name);
}

export function useShareChannels(): readonly ShareChannel[] {
	const buttons = useQueryParamValue('buttons');
	const {
		data: {
			initial,
			isTiktokChannelEnabled,
			petition: { geoReach },
			featureConfigs: { shareButtonsListInitial, shareButtonsListNonInitial, psfOnlyShareButtonList },
		},
	} = usePsfSharePageData();

	const channels = useMemo(() => {
		const fromQuery = mapOldShareChannels(
			(buttons ? buttons.split(',') : []) as ReadonlyArray<OldShareChannelName | ShareChannel>,
		).filter((name) => SHARE_CHANNELS[name]);

		if (fromQuery.length > 0) {
			return fromQuery;
		}

		// FIXME we have 3 FCs that list the channels in different ways, we should unify them
		if (psfOnlyShareButtonList.length > 0) {
			return psfOnlyShareButtonList as readonly ShareChannel[];
		}

		return initial
			? (shareButtonsListInitial as readonly ShareChannel[])
			: mapOldShareChannels(shareButtonsListNonInitial);
	}, [buttons, initial, shareButtonsListInitial, shareButtonsListNonInitial, psfOnlyShareButtonList]);

	const filters = useMemo(
		() => ({
			...SHARE_CHANNELS,
			nextdoor: geoReach === 'local',
			tiktok: isTiktokChannelEnabled,
		}),
		[geoReach, isTiktokChannelEnabled],
	);

	const remappedShareChannels = usePsfShareChannelRemap(channels);
	return useFilteredShareChannels(remappedShareChannels, filters);
}
